// src/Pages/Landing/Users/index.jsx
// SEO-optimized discovery landing page
// Value props: Trust Chain, Smart Rating, network-based discovery
// Target: people looking for trusted local businesses

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import EnhancedSEO from '../../../Components/SEO/EnhancedSEO';
import {
  Box, Container, Grid, Typography, Button, Chip, Stack,
  Divider, useTheme, useMediaQuery, Avatar, Rating,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  Hub as NetworkIcon,
  CheckCircle as CheckIcon,
  PersonSearch as PersonSearchIcon,
  ThumbUp as ThumbUpIcon,
  Explore as ExploreIcon,
} from '@mui/icons-material';

// ─── Design tokens ─────────────────────────────────────────────────────────────

const BRAND = {
  navy:    '#0D2B6B',
  blue:    '#1976D2',
  green:   '#2E7D32',
  lightBg: '#F3F9FF',
  slate:   '#455A7B',
  muted:   '#90A4AE',
  border:  '#E2E8F0',
};

// ─── Data ──────────────────────────────────────────────────────────────────────

const WHY_REVFER = [
  {
    Icon:      NetworkIcon,
    title:     'Trust Chain',
    headline:  'See exactly who vouches for a business.',
    body:      'Every review on Revfer is connected to a real person in a real network. You can see if the reviewer is a friend of a friend, a colleague, or someone three connections away. Not anonymous strangers — actual people linked to you.',
    stat:      '3 hops',
    statLabel: 'average trust distance',
    color:     '#1565C0',
    bg:        'rgba(21,101,192,0.07)',
    examples:  [
      'See your connection path to every reviewer',
      'Filter businesses by how close the reviewers are to you',
      'Connections you trust carry more weight',
    ],
  },
  {
    Icon:      StarIcon,
    title:     'Smart Rating',
    headline:  'A rating that actually means something.',
    body:      'A 4.2 from 3 people in your network tells you more than a 4.8 from 300 strangers. Revfer weights ratings by reviewer trust score and connection distance — so the number you see reflects people like you, not the internet at large.',
    stat:      'Network-weighted',
    statLabel: 'not crowd-averaged',
    color:     '#E65100',
    bg:        'rgba(230,81,0,0.07)',
    examples:  [
      'Ratings weighted by reviewer trust score',
      'Connection distance factors into every score',
      'See separate network rating vs overall rating',
    ],
  },
  {
    Icon:      SearchIcon,
    title:     'Network Discovery',
    headline:  'Find what your actual network recommends.',
    body:      'Search for a restaurant, a plumber, a dentist — and see which ones people in your network have actually used and reviewed. Not paid ads. Not SEO games. Just real recommendations from people connected to you.',
    stat:      'Zero ads',
    statLabel: 'purely network-driven',
    color:     '#2E7D32',
    bg:        'rgba(46,125,50,0.07)',
    examples:  [
      'Filter by your network activity',
      'See who in your network reviewed each business',
      'Trending businesses in your area and network',
    ],
  },
];

const HOW_IT_WORKS = [
  {
    Icon:  PersonSearchIcon,
    step:  '01',
    title: 'Search for what you need',
    body:  'Restaurant, salon, plumber, dentist — search by type or name. Revfer shows you results ranked by your network activity, not by who paid for placement.',
    time:  '30 sec',
  },
  {
    Icon:  NetworkIcon,
    step:  '02',
    title: 'See who in your network reviewed it',
    body:  'For every business, you can see the trust chain — how many connections away the reviewers are, and what their trust score is. A review from someone three connections away is worth more than a review from a stranger.',
    time:  '1 min',
  },
  {
    Icon:  ThumbUpIcon,
    step:  '03',
    title: 'Make a decision you can trust',
    body:  'Book, call or visit — knowing the recommendation came from real people in your actual network, not a paid review farm. Then leave your own review and help the people connected to you.',
    time:  'Confident',
  },
];

const COMPARISON = [
  { aspect: 'Who reviews',          others: 'Anonymous strangers',         revfer: 'People in your network'         },
  { aspect: 'Rating calculation',   others: 'Simple average of all stars', revfer: 'Weighted by trust and distance' },
  { aspect: 'Business ranking',     others: 'Paid placement and SEO',      revfer: 'Network activity and trust'     },
  { aspect: 'Reviewer credibility', others: 'Unknown',                     revfer: 'Verified trust score shown'     },
  { aspect: 'Connection to you',    others: 'None',                        revfer: 'Always shown'                   },
];

// ─── Sub-components ────────────────────────────────────────────────────────────

const FeatureCard = ({ feature }) => {
  const { Icon, title, headline, body, stat, statLabel, color, bg, examples } = feature;
  return (
    <Box sx={{
      bgcolor:      'white',
      borderRadius: 3,
      p:            { xs: 3, md: 4 },
      border:       `1px solid ${BRAND.border}`,
      height:       '100%',
      display:      'flex',
      flexDirection: 'column',
      transition:   'all 0.25s ease',
      '&:hover':    { transform: 'translateY(-4px)', boxShadow: `0 12px 32px ${color}18`, borderColor: `${color}40` },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ color, fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color, letterSpacing: 1.5, fontSize: 10, fontWeight: 700, display: 'block', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.navy, lineHeight: 1.3, fontSize: { xs: 17, md: 19 } }}>
            {headline}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.75, mb: 2.5, fontSize: 14, flex: 1 }}>
        {body}
      </Typography>

      <Box sx={{ mb: 2.5 }}>
        {examples.map(ex => (
          <Box key={ex} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
            <CheckIcon sx={{ color, fontSize: 16, mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: BRAND.slate, fontSize: 13 }}>{ex}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{
        display:      'inline-flex',
        alignSelf:    'flex-start',
        alignItems:   'baseline',
        gap:          0.75,
        bgcolor:      bg,
        borderRadius: 2,
        px:           2, py: 1,
        border:       `1px solid ${color}20`
      }}>
        <Typography sx={{ fontWeight: 800, color, fontSize: 16, lineHeight: 1 }}>{stat}</Typography>
        <Typography variant="caption" sx={{ color, fontWeight: 600, opacity: 0.8 }}>{statLabel}</Typography>
      </Box>
    </Box>
  );
};

const ComparisonTable = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BRAND.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
      <Box sx={{ bgcolor: BRAND.navy, px: 3, py: 2, display: 'flex' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: 1.5, flex: 1.5 }}>
          HOW REVIEWS WORK
        </Typography>
        {!isMobile && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: 1.5, flex: 1, textAlign: 'center' }}>
            OTHER PLATFORMS
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: 1.5, flex: 1, textAlign: 'right' }}>
          REVFER
        </Typography>
      </Box>

      {COMPARISON.map(({ aspect, others, revfer }, i) => (
        <Box key={aspect} sx={{ display: 'flex', px: 3, py: 1.75, bgcolor: i % 2 === 0 ? 'white' : '#F8FAFC', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ flex: 1.5, fontWeight: 500, color: BRAND.slate, fontSize: 13 }}>
            {aspect}
          </Typography>
          {!isMobile && (
            <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 500, color: '#90A4AE', fontSize: 13 }}>
              {others}
            </Typography>
          )}
          <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', fontWeight: 700, color: BRAND.blue, fontSize: 13 }}>
            {revfer}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const UsersLandingPage = () => {
  const navigate       = useNavigate();
  const theme          = useTheme();
  const isMobile       = useMediaQuery(theme.breakpoints.down('md'));
  const { authStatus } = useAuthenticator((c) => [c.authStatus]);

  const handleCTA = () => {
    if (authStatus === 'authenticated') {
      navigate('/find');
    } else {
      navigate('/auth/signin', { state: { name: 'discovery', link: '/find' } });
    }
  };

  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What is Revfer for consumers?",
        "acceptedAnswer": { "@type": "Answer", "text": "Revfer helps you find local businesses recommended by people in your actual network — not anonymous strangers. Every review is connected to a real person, and ratings are weighted by how closely connected the reviewer is to you." } },
      { "@type": "Question", "name": "What is Trust Chain?",
        "acceptedAnswer": { "@type": "Answer", "text": "Trust Chain shows you the connection path between you and every reviewer. A review from someone two connections away from you carries more weight than a review from a stranger. This means the businesses Revfer surfaces are ones your actual network has vouched for." } },
      { "@type": "Question", "name": "What is Smart Rating?",
        "acceptedAnswer": { "@type": "Answer", "text": "Smart Rating weights a business's star rating by the trust score and connection distance of each reviewer. A 4.2 from people in your network is more meaningful than a 4.8 from anonymous reviewers, and Revfer's Smart Rating reflects that." } },
      { "@type": "Question", "name": "Is Revfer free to use for finding businesses?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Discovering businesses, reading reviews and seeing your Trust Chain connections is completely free." } },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org", "@type": "WebApplication",
    "name": "Revfer — Discover businesses your network trusts",
    "url": "https://www.revfer.com",
    "applicationCategory": "LocalBusinessDiscovery",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
  };

  return (
    <>
      <EnhancedSEO
        title="Revfer — Find local businesses your network actually trusts"
        description="Discover restaurants, salons, contractors and local businesses recommended by people in your real network — not anonymous strangers. Trust Chain shows you exactly who vouches for every business. Free to use."
        keywords="trusted local business recommendations, network-based reviews, find restaurants near me, local business discovery, trusted reviews, community recommendations"
        type="website"
        image="/og-image.jpg"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Discover Local Businesses', url: '/landing/users' }]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      {/* ═══ HERO ════════════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          background: `linear-gradient(150deg, #0D2B6B 0%, #1A3A7A 50%, #2E7D32 100%)`,
          color:    'white',
          pt:       { xs: 8, md: 12 },
          pb:       { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04, backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">

            {/* Left */}
            <Grid item xs={12} md={7}>
              <Chip
                label="Network-powered local discovery"
                size="small"
                sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: 12, border: '1px solid rgba(255,255,255,0.2)' }}
              />

              <Typography
                component="h1"
                variant="h1"
                sx={{ fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem' }, fontWeight: 800, lineHeight: 1.15, mb: 3 }}
              >
                Find businesses your{' '}
                <Box component="span" sx={{ background: 'linear-gradient(90deg, #81D4FA, #A5F3FC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  real network
                </Box>{' '}
                actually trusts.
              </Typography>

              <Typography variant="h6" sx={{ opacity: 0.85, lineHeight: 1.7, mb: 4, fontWeight: 400, fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: 520 }}>
                Not 300 anonymous strangers giving 4.8 stars.
                The restaurant your colleague's friend loved last month.
                The plumber three connections away who fixed it right the first time.
                Revfer shows you who vouches for every business.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 4 }}>
                <Button
                  variant="contained" size="large" endIcon={<ArrowIcon />} onClick={handleCTA}
                  sx={{ bgcolor: 'white', color: BRAND.blue, fontWeight: 700, fontSize: 16, px: 4, py: 2, borderRadius: 3, textTransform: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', transform: 'translateY(-2px)' } }}
                >
                  {authStatus === 'authenticated' ? 'Discover Businesses' : 'Join Free — Find Trusted Businesses'}
                </Button>
                <Typography variant="body2" sx={{ opacity: 0.65, fontSize: 13, textAlign: { xs: 'center', sm: 'left' } }}>
                  Free to use · No credit card · Takes 2 minutes
                </Typography>
              </Stack>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {[
                  { value: '13,000+', label: 'people on the network' },
                  { value: '3 hops',  label: 'average trust distance' },
                  { value: 'Zero ads', label: 'in search results'     },
                ].map(({ value, label }) => (
                  <Box key={label}>
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1, mb: 0.25 }}>{value}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 500 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right — trust chain visual */}
            <Grid item xs={12} md={5}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 4, p: 3.5, border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)' }}>
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2, fontSize: 10, fontWeight: 700, display: 'block', mb: 2.5 }}>
                  TRUST CHAIN EXAMPLE
                </Typography>

                {/* You */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#1976D2', fontSize: 14, fontWeight: 700 }}>You</Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: 13 }}>You</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>Looking for a dentist</Typography>
                  </Box>
                </Box>

                <Box sx={{ width: 2, height: 16, bgcolor: 'rgba(255,255,255,0.2)', ml: 2.2, mb: 1 }} />

                {/* 1st degree */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#2E7D32', fontSize: 12, fontWeight: 700 }}>SJ</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: 13 }}>Sarah J.</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>Your direct connection</Typography>
                  </Box>
                  <Chip label="1st" size="small" sx={{ bgcolor: 'rgba(46,125,50,0.3)', color: '#A5D6A7', fontSize: 10, height: 18, fontWeight: 700 }} />
                </Box>
                <Box sx={{ ml: 6, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#A5D6A7', fontSize: 11 }}>★★★★★ "Dr. Chen is incredible — thorough and gentle."</Typography>
                </Box>

                <Box sx={{ width: 2, height: 16, bgcolor: 'rgba(255,255,255,0.2)', ml: 2.2, mb: 1 }} />

                {/* 2nd degree */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#E65100', fontSize: 12, fontWeight: 700 }}>MR</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: 13 }}>Marcus R.</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>Friend of Sarah</Typography>
                  </Box>
                  <Chip label="2nd" size="small" sx={{ bgcolor: 'rgba(230,81,0,0.3)', color: '#FFCC80', fontSize: 10, height: 18, fontWeight: 700 }} />
                </Box>
                <Box sx={{ ml: 6, mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#FFCC80', fontSize: 11 }}>★★★★★ "Best dentist I've had in 10 years."</Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 1.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: 13 }}>
                      Dr. Chen Dental
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                      Smart Rating: 4.9 · 2 network reviews
                    </Typography>
                  </Box>
                  <Rating value={5} readOnly size="small" sx={{ '& .MuiSvgIcon-root': { color: '#FFD700', fontSize: 14 } }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══ THE PROBLEM ═════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: '#FFFBF0', py: { xs: 6, md: 8 }, borderBottom: '1px solid #FFE082' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#5D4037', textAlign: 'center', mb: 1.5, fontSize: { xs: '1.3rem', md: '1.6rem' } }}>
            A 4.7-star rating used to mean something.
          </Typography>
          <Typography variant="body1" sx={{ color: '#6D4C41', textAlign: 'center', maxWidth: 600, mx: 'auto', lineHeight: 1.7 }}>
            Now you don't know if those reviews are from real customers, paid posters
            or the owner's family. You end up calling a friend anyway —
            "do you know a good plumber around here?"
            Revfer makes that network searchable.
          </Typography>
        </Container>
      </Box>

      {/* ═══ THREE FEATURES ══════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: BRAND.lightBg }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Chip
              label="How Revfer works"
              size="small"
              sx={{ mb: 2, bgcolor: 'rgba(25,118,210,0.1)', color: BRAND.blue, fontWeight: 700, border: '1px solid rgba(25,118,210,0.2)' }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Reviews from people connected to you.<br />
              Ratings weighted by trust.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 520, mx: 'auto', lineHeight: 1.75 }}>
              Three features that make Revfer recommendations
              worth more than a star average from strangers.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {WHY_REVFER.map((feature) => (
              <Grid key={feature.title} item xs={12} md={4}>
                <FeatureCard feature={feature} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══ COMPARISON TABLE ════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
            <Chip
              label="Why it's different"
              size="small"
              sx={{ mb: 2, bgcolor: 'rgba(46,125,50,0.08)', color: BRAND.green, fontWeight: 700, border: '1px solid rgba(46,125,50,0.2)' }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Not another review platform.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 500, mx: 'auto', lineHeight: 1.75 }}>
              Yelp, Google and TripAdvisor aggregate strangers.
              Revfer surfaces your network.
            </Typography>
          </Box>
          <ComparisonTable />
        </Container>
      </Box>

      {/* ═══ HOW IT WORKS ════════════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: BRAND.lightBg }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
            <Chip
              label="Get started"
              size="small"
              sx={{ mb: 2, bgcolor: 'rgba(46,125,50,0.1)', color: BRAND.green, fontWeight: 700, border: '1px solid rgba(46,125,50,0.2)' }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              2 minutes to see what your network recommends.
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 440, mx: 'auto', lineHeight: 1.7 }}>
              Free to use. No credit card. No algorithm deciding what you see.
            </Typography>
          </Box>

          <Stack spacing={3}>
            {HOW_IT_WORKS.map(({ Icon, step, title, body, time }, i) => (
              <Box
                key={step}
                sx={{
                  display:      'flex',
                  gap:          { xs: 2.5, md: 4 },
                  alignItems:   'flex-start',
                  p:            { xs: 3, md: 4 },
                  bgcolor:      'white',
                  borderRadius: 3,
                  border:       `1px solid ${BRAND.border}`,
                  transition:   'all 0.2s',
                  '&:hover':    { boxShadow: '0 8px 24px rgba(0,0,0,0.06)', borderColor: '#BBDEFB' },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: BRAND.lightBg, border: '1px solid #BBDEFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ color: BRAND.blue, fontSize: 22 }} />
                  </Box>
                  {i < HOW_IT_WORKS.length - 1 && (
                    <Box sx={{ width: 2, height: 24, bgcolor: '#BBDEFB', borderRadius: 1 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="overline" sx={{ color: BRAND.muted, letterSpacing: 2, fontSize: 10, fontWeight: 700 }}>
                      STEP {step}
                    </Typography>
                    <Chip
                      label={`⏱ ${time}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(46,125,50,0.08)', color: BRAND.green, fontWeight: 700, fontSize: 11, height: 22 }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.navy, mb: 0.75, fontSize: { xs: 16, md: 18 } }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.slate, lineHeight: 1.7, fontSize: 14 }}>
                    {body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ═══ WHAT YOU CAN FIND ═══════════════════════════════════════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: BRAND.navy, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              What can you find on Revfer?
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.slate, maxWidth: 440, mx: 'auto', lineHeight: 1.7 }}>
              Any local business your network has visited, reviewed or recommended.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {[
              { emoji: '🍽️', label: 'Restaurants and cafes'         },
              { emoji: '💇', label: 'Salons and spas'               },
              { emoji: '🔧', label: 'Plumbers and contractors'       },
              { emoji: '🦷', label: 'Dentists and healthcare'        },
              { emoji: '🚗', label: 'Auto repair and services'       },
              { emoji: '🏋️', label: 'Gyms and fitness studios'      },
              { emoji: '🐾', label: 'Vets and pet services'         },
              { emoji: '⚖️', label: 'Legal and professional services'},
              { emoji: '🌿', label: 'Landscaping and home services'  },
              { emoji: '📚', label: 'Tutors and education'           },
              { emoji: '🛍️', label: 'Retail and specialty shops'    },
              { emoji: '🏠', label: 'Real estate and property'       },
            ].map(({ emoji, label }) => (
              <Grid key={label} item xs={6} sm={4} md={3}>
                <Box sx={{
                  p:            2,
                  borderRadius: 2,
                  border:       `1px solid ${BRAND.border}`,
                  textAlign:    'center',
                  transition:   'all 0.2s',
                  bgcolor:      'white',
                  '&:hover':    { borderColor: BRAND.blue, bgcolor: BRAND.lightBg, transform: 'translateY(-2px)' }
                }}>
                  <Typography variant="h5" sx={{ mb: 0.5 }}>{emoji}</Typography>
                  <Typography variant="body2" sx={{ color: BRAND.slate, fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}>
                    {label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══ BOTTOM CTA ══════════════════════════════════════════════════════ */}
      <Box
        component="section"
        sx={{
          py:         { xs: 8, md: 10 },
          background: `linear-gradient(150deg, #0D2B6B 0%, #2E7D32 100%)`,
          color:      'white',
          textAlign:  'center',
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2 }}>
            Stop asking strangers.<br />
            <Box component="span" sx={{ color: '#A5F3FC' }}>Ask your network.</Box>
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.75, mb: 5, lineHeight: 1.7 }}>
            The plumber your colleague swears by. The restaurant your neighbor keeps going back to.
            The dentist three connections away with a perfect record.
            They're already on Revfer.
          </Typography>
          <Button
            variant="contained" size="large" endIcon={<ArrowIcon />} onClick={handleCTA}
            sx={{ bgcolor: 'white', color: BRAND.blue, fontWeight: 700, fontSize: 17, px: 5, py: 2.25, borderRadius: 3, textTransform: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', transform: 'translateY(-2px)' } }}
          >
            {authStatus === 'authenticated' ? 'Discover Businesses' : 'Join Free — See Your Network'}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.5 }}>
            Free to use · No credit card · 2 minute setup
          </Typography>

          {/* Cross-link to business side */}
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
            <Typography variant="body2" sx={{ opacity: 0.6, mb: 1 }}>
              Own a business?
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/landing/business')}
              sx={{ color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              See Revfer for business owners →
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default UsersLandingPage;